import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from "@/lib/utils";
import {
  Zap,
  Factory,
  Shield,
  Activity,
  Thermometer,
  Gauge,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Bell,
  Users,
  Calendar,
  MapPin,
  DoorOpen,
  DoorClosed,
  UserCheck,
  UserX,
  Key,
  Router,
  Signal,
  Battery,
  Fuel,
  GaugeCircle,
  Settings,
  History,
  BarChart3,
  TrendingUp,
  Power,
  Droplets,
  Wrench,
  Building2,
  Layers,
  Map,
  Navigation,
  Globe,
  Cpu,
  Server,
  Network,
  HardDrive,
  CircuitBoard,
  Wifi,
  ToggleLeft,
  ToggleRight,
  Radio,
  Waves,
  ArrowUpDown,
  RefreshCw,
  Database,
  Car,
  Truck,
  MapPinned,
  Route,
  Milestone,
  Timer,
  Compass,
  Play,
  Pause,
  Square,
  AlertCircle,
  User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarInset,
} from "@/components/ui/sidebar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import HomeHeader from '../../layout/HomeHeader/HomeHeader.component';
import Footer from '../../layout/Footer/Footer.component';

// Asset Types
const ASSETS = [
  { id: 'diesel-generator', name: 'Diesel Generator', icon: Zap, color: 'amber' },
  { id: 'factory-access', name: 'Factory Access Control', icon: Shield, color: 'purple' },
  { id: 'scada-gateway', name: 'SCADA Gateway', icon: Cpu, color: 'cyan' },
  { id: 'fleet-management', name: 'Fleet Management', icon: Truck, color: 'blue' },
];

// Generate diesel generator data
const generateGeneratorData = () => {
  return Array.from({ length: 20 }, (_, i) => ({
    time: new Date(Date.now() - (19 - i) * 3000).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    voltage: Math.round((220 + Math.sin(i * 0.4) * 10 + Math.random() * 3) * 10) / 10,
    current: Math.round((45 + Math.cos(i * 0.3) * 8 + Math.random() * 2) * 10) / 10,
    frequency: Math.round((50 + Math.sin(i * 0.2) * 0.5 + Math.random() * 0.2) * 100) / 100,
    temperature: Math.round((75 + Math.sin(i * 0.5) * 15 + Math.random() * 5) * 10) / 10,
    fuelLevel: Math.max(0, Math.min(100, Math.round((60 + Math.cos(i * 0.1) * 5 + Math.random() * 2) * 10) / 10)),
    oilPressure: Math.round((45 + Math.sin(i * 0.3) * 8 + Math.random() * 3) * 10) / 10,
  }));
};

// Generate access control events
const generateAccessEvents = () => {
  const users = ['Ahmed Hassan', 'Fatima Al-Mansouri', 'Mohammed Ali', 'Sara Ibrahim', 'Omar Khaled', 'Layla Ahmed'];
  const locations = ['Main Gate', 'Production Floor', 'Warehouse', 'Office Building', 'Maintenance Bay', 'Quality Control'];
  const events = [];
  
  for (let i = 0; i < 15; i++) {
    const timestamp = new Date(Date.now() - (14 - i) * 300000);
    const isGranted = Math.random() > 0.12;
    events.push({
      id: i + 1,
      time: timestamp.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
      user: users[Math.floor(Math.random() * users.length)],
      location: locations[Math.floor(Math.random() * locations.length)],
      status: isGranted ? 'granted' : 'denied',
      method: Math.random() > 0.4 ? 'Card' : 'Biometric',
      icon: isGranted ? UserCheck : UserX
    });
  }
  return events.reverse();
};

// Generate SCADA telemetry data
const generateScadaData = () => {
  return Array.from({ length: 20 }, (_, i) => ({
    time: new Date(Date.now() - (19 - i) * 3000).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    // Analog inputs (4-20mA sensors)
    pressure: Math.round((4.5 + Math.sin(i * 0.3) * 1.5 + Math.random() * 0.3) * 100) / 100,
    flowRate: Math.round((120 + Math.cos(i * 0.4) * 30 + Math.random() * 5) * 10) / 10,
    level: Math.round((65 + Math.sin(i * 0.2) * 15 + Math.random() * 3) * 10) / 10,
    temperature: Math.round((45 + Math.sin(i * 0.5) * 10 + Math.random() * 2) * 10) / 10,
    // Modbus registers
    motorSpeed: Math.round(1450 + Math.sin(i * 0.3) * 50 + Math.random() * 10),
    pumpStatus: Math.random() > 0.1 ? 1 : 0,
    valvePosition: Math.round((50 + Math.cos(i * 0.2) * 25 + Math.random() * 5) * 10) / 10,
  }));
};

// Provisioning modal used by any asset
function ProvisioningModal({ open, onOpenChange, assetLabel = "Gateway" }) {
  const steps = [
    { title: "Enter Device ID", detail: "Scan or type the serial/IMEI" },
    { title: "Configure Network", detail: "Select 4G/LTE or Ethernet & APN" },
    { title: "Assign to Site", detail: "Bind to Factory Line A / Zone" },
    { title: "Verify Telemetry", detail: "Ping device & start data stream" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white border border-gray-200 shadow-2xl p-6 rounded-xl">
        <DialogHeader>
          <DialogTitle>{assetLabel} Provisioning</DialogTitle>
          <DialogDescription>
            Quick setup to bring the device online and start streaming data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder={`${assetLabel} ID / Serial`} defaultValue="IGW-4G-2024-0892" />
            <Input placeholder="Project / Site" defaultValue="Factory Line A" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="justify-start">
              <Signal className="w-4 h-4 mr-2 text-emerald-600" />
              4G / LTE (Auto)
            </Button>
            <Button variant="outline" className="justify-start">
              <Router className="w-4 h-4 mr-2 text-blue-600" />
              Ethernet / Static IP
            </Button>
          </div>

          <div className="space-y-2">
            {steps.map((step, idx) => (
              <div
                key={step.title}
                className="flex items-start gap-3 p-3 rounded-lg border bg-gray-50"
              >
                <Badge variant="outline" className="mt-0.5">{idx + 1}</Badge>
                <div>
                  <div className="font-semibold text-sm">{step.title}</div>
                  <div className="text-xs text-gray-600">{step.detail}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button onClick={() => onOpenChange(false)}>
              Start Provisioning
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Diesel Generator Dashboard Component
function DieselGeneratorDashboard() {
  const [generatorData, setGeneratorData] = useState(generateGeneratorData());
  const [isRunning, setIsRunning] = useState(true);
  const [runtime, setRuntime] = useState(1247); // hours
  const [maintenanceDue, setMaintenanceDue] = useState(false);
  const [provOpen, setProvOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setGeneratorData(prev => {
        const newPoint = {
          time: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
          voltage: Math.round((220 + Math.sin(Date.now() / 5000) * 10 + Math.random() * 3) * 10) / 10,
          current: Math.round((45 + Math.cos(Date.now() / 7000) * 8 + Math.random() * 2) * 10) / 10,
          frequency: Math.round((50 + Math.sin(Date.now() / 9000) * 0.5 + Math.random() * 0.2) * 100) / 100,
          temperature: Math.round((75 + Math.sin(Date.now() / 5000) * 15 + Math.random() * 5) * 10) / 10,
          fuelLevel: Math.max(0, Math.min(100, Math.round((60 + Math.cos(Date.now() / 10000) * 5 + Math.random() * 2) * 10) / 10)),
          oilPressure: Math.round((45 + Math.sin(Date.now() / 6000) * 8 + Math.random() * 3) * 10) / 10,
        };
        return [...prev.slice(1), newPoint];
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const current = generatorData[generatorData.length - 1];
  const statusColor = isRunning ? 'emerald' : 'gray';
  const tempStatus = current?.temperature > 90 ? 'critical' : current?.temperature > 80 ? 'warning' : 'normal';

  const metrics = [
    { label: 'Voltage', value: `${current?.voltage || 0}V`, icon: Zap, color: 'blue', status: 'normal' },
    { label: 'Current', value: `${current?.current || 0}A`, icon: Activity, color: 'emerald', status: 'normal' },
    { label: 'Frequency', value: `${current?.frequency || 0}Hz`, icon: GaugeCircle, color: 'purple', status: 'normal' },
    { label: 'Temperature', value: `${current?.temperature || 0}°C`, icon: Thermometer, color: tempStatus === 'critical' ? 'red' : tempStatus === 'warning' ? 'amber' : 'emerald', status: tempStatus },
    { label: 'Fuel Level', value: `${current?.fuelLevel || 0}%`, icon: Fuel, color: current?.fuelLevel < 20 ? 'red' : current?.fuelLevel < 40 ? 'amber' : 'emerald', status: current?.fuelLevel < 20 ? 'critical' : 'normal' },
    { label: 'Oil Pressure', value: `${current?.oilPressure || 0} PSI`, icon: Droplets, color: 'blue', status: 'normal' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Diesel Generator Monitoring</h2>
          <p className="text-gray-500">Generator Unit #DG-2024-001 | Industrial Plant</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={cn(
            "text-white",
            isRunning ? "bg-emerald-500" : "bg-gray-500"
          )}>
            <span className={cn("w-2 h-2 rounded-full mr-2", isRunning && "animate-pulse")} />
            {isRunning ? 'Running' : 'Stopped'}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setProvOpen(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      <ProvisioningModal open={provOpen} onOpenChange={setProvOpen} assetLabel="Generator Gateway" />

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          const colorClasses = {
            blue: 'bg-blue-50 border-blue-200 text-blue-600',
            emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600',
            purple: 'bg-purple-50 border-purple-200 text-purple-600',
            red: 'bg-red-50 border-red-200 text-red-600',
            amber: 'bg-amber-50 border-amber-200 text-amber-600',
          };
          return (
            <Card key={metric.label} className={cn("border-2", colorClasses[metric.color])}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Icon className="w-5 h-5" />
                  {metric.status === 'critical' && <AlertTriangle className="w-4 h-4 text-red-600" />}
                </div>
                <div className="text-2xl font-bold mb-1">{metric.value}</div>
                <div className="text-xs text-gray-600">{metric.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Voltage & Current */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-blue-600" />
              Voltage & Current
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={generatorData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="voltageGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="currentGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" fontSize={10} stroke="#9ca3af" />
                <YAxis fontSize={10} stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                <Area type="monotone" dataKey="voltage" stroke="#3b82f6" strokeWidth={2} fill="url(#voltageGrad)" name="Voltage (V)" />
                <Area type="monotone" dataKey="current" stroke="#10b981" strokeWidth={2} fill="url(#currentGrad)" name="Current (A)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Temperature & Fuel */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Thermometer className="w-5 h-5 text-red-600" />
              Temperature & Fuel Level
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={generatorData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" fontSize={10} stroke="#9ca3af" />
                <YAxis fontSize={10} stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                <ReferenceLine y={90} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Alert: 90°C', fontSize: 10, fill: '#ef4444' }} />
                <Line type="monotone" dataKey="temperature" stroke="#ef4444" strokeWidth={2} dot={false} name="Temperature (°C)" />
                <Line type="monotone" dataKey="fuelLevel" stroke="#f59e0b" strokeWidth={2} dot={false} name="Fuel Level (%)" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Location View */}
      <Card className="bg-white shadow-lg overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Map className="w-5 h-5 text-blue-600" />
            Generator Location
          </CardTitle>
          <CardDescription>Hassi Messaoud Industrial Zone, Algeria</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-1 lg:grid-cols-3">
            {/* Map */}
            <div className="lg:col-span-2 h-[300px] relative">
              <iframe
                title="Generator Location Map"
                width="100%"
                height="100%"
                frameBorder="0"
                scrolling="no"
                marginHeight="0"
                marginWidth="0"
                src="https://www.openstreetmap.org/export/embed.html?bbox=5.9%2C31.5%2C6.3%2C31.9&layer=mapnik&marker=31.68%2C6.07"
                style={{ border: 0 }}
              />
              <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-2 text-xs">
                <Badge className="bg-emerald-500 text-white mb-1">
                  <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse" />
                  GPS Active
                </Badge>
              </div>
            </div>
            
            {/* Location Details */}
            <div className="p-6 bg-gradient-to-br from-slate-50 to-white border-l">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wide mb-1">
                    <MapPin className="w-3 h-3" />
                    Site Location
                  </div>
                  <div className="font-semibold text-gray-900">Hassi Messaoud</div>
                  <div className="text-sm text-gray-600">Industrial Zone B, Sector 4</div>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wide mb-1">
                    <Globe className="w-3 h-3" />
                    Coordinates
                  </div>
                  <div className="font-mono text-sm text-gray-900">31.6800° N, 6.0700° E</div>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wide mb-1">
                    <Navigation className="w-3 h-3" />
                    Region
                  </div>
                  <div className="text-sm text-gray-900">Ouargla Province, Algeria</div>
                </div>

                <div className="pt-2 border-t">
                  <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wide mb-2">
                    <Building2 className="w-3 h-3" />
                    Site Info
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Facility:</span>
                      <span className="font-medium text-gray-900">Oil & Gas Field</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Timezone:</span>
                      <span className="font-medium text-gray-900">UTC+1 (CET)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Elevation:</span>
                      <span className="font-medium text-gray-900">142m</span>
                    </div>
                  </div>
                </div>

                <Button variant="outline" size="sm" className="w-full mt-2" asChild>
                  <a 
                    href="https://www.openstreetmap.org/?mlat=31.68&mlon=6.07#map=12/31.68/6.07" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Map className="w-4 h-4 mr-2" />
                    Open in Maps
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status & Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Power className="w-8 h-8" />
              <Badge className="bg-white/20 text-white">Active</Badge>
            </div>
            <div className="text-3xl font-bold mb-1">{runtime}h</div>
            <div className="text-sm opacity-90">Total Runtime</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Wrench className="w-6 h-6 text-gray-600" />
              <Badge variant={maintenanceDue ? "destructive" : "outline"}>
                {maintenanceDue ? 'Due' : 'Scheduled'}
              </Badge>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">Next: 1500h</div>
            <div className="text-sm text-gray-500">Maintenance Schedule</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Signal className="w-6 h-6 text-emerald-600" />
              <Badge className="bg-emerald-500 text-white">Online</Badge>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Gateway: IGW-4G-2024-0892</div>
              <div>Signal: -62 dBm</div>
              <div>Last Update: {new Date().toLocaleTimeString()}</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Factory Access Control Dashboard Component
function FactoryAccessControlDashboard() {
  const [accessEvents, setAccessEvents] = useState(generateAccessEvents());
  const [doorStatus, setDoorStatus] = useState({ 
    mainGate: 'closed', 
    production: 'open', 
    warehouse: 'closed',
    office: 'open',
    maintenance: 'closed'
  });
  const [activeUsers, setActiveUsers] = useState(18);
  const [todayAccess, setTodayAccess] = useState(342);
  const [provOpen, setProvOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const newEvent = {
        id: accessEvents.length + 1,
        time: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
        user: ['Ahmed Hassan', 'Fatima Al-Mansouri', 'Mohammed Ali', 'Sara Ibrahim', 'Omar Khaled', 'Layla Ahmed'][Math.floor(Math.random() * 6)],
        location: ['Main Gate', 'Production Floor', 'Warehouse', 'Office Building', 'Maintenance Bay', 'Quality Control'][Math.floor(Math.random() * 6)],
        status: Math.random() > 0.12 ? 'granted' : 'denied',
        method: Math.random() > 0.4 ? 'Card' : 'Biometric',
        icon: Math.random() > 0.12 ? UserCheck : UserX
      };
      setAccessEvents(prev => [newEvent, ...prev.slice(0, 14)]);
      setTodayAccess(prev => prev + 1);
    }, 3500);
    return () => clearInterval(interval);
  }, [accessEvents.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      const doors = Object.keys(doorStatus);
      const randomDoor = doors[Math.floor(Math.random() * doors.length)];
      setDoorStatus(prev => ({
        ...prev,
        [randomDoor]: prev[randomDoor] === 'open' ? 'closed' : 'open'
      }));
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const entryPoints = [
    { id: 'mainGate', name: 'Main Gate', status: doorStatus.mainGate, icon: Building2, color: 'blue' },
    { id: 'production', name: 'Production Floor', status: doorStatus.production, icon: Factory, color: 'emerald' },
    { id: 'warehouse', name: 'Warehouse', status: doorStatus.warehouse, icon: Building2, color: 'purple' },
    { id: 'office', name: 'Office Building', status: doorStatus.office, icon: Building2, color: 'amber' },
    { id: 'maintenance', name: 'Maintenance Bay', status: doorStatus.maintenance, icon: Wrench, color: 'red' },
  ];

  const stats = [
    { label: 'Active Users', value: activeUsers, icon: Users, color: 'emerald' },
    { label: 'Today Access', value: todayAccess, icon: Calendar, color: 'blue' },
    { label: 'Granted', value: Math.floor(todayAccess * 0.91), icon: UserCheck, color: 'emerald' },
    { label: 'Denied', value: Math.floor(todayAccess * 0.09), icon: UserX, color: 'red' },
  ];

  // Access by hour data
  const accessByHour = Array.from({ length: 12 }, (_, i) => ({
    hour: `${String(i * 2).padStart(2, '0')}:00`,
    granted: Math.floor(Math.random() * 30 + 10),
    denied: Math.floor(Math.random() * 5 + 1),
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Factory Access Control</h2>
          <p className="text-gray-500">Industrial Plant Security System | ACC-CTL-0567</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-purple-500 text-white">
            <Shield className="w-3 h-3 mr-1" />
            ACC-CTL-0567
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setProvOpen(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      <ProvisioningModal open={provOpen} onOpenChange={setProvOpen} assetLabel="Access Gateway" />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const colorClasses = {
            emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600',
            blue: 'bg-blue-50 border-blue-200 text-blue-600',
            red: 'bg-red-50 border-red-200 text-red-600',
          };
          return (
            <Card key={stat.label} className={cn("border-2", colorClasses[stat.color])}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-2xl font-bold">{stat.value}</div>
                <div className="text-xs text-gray-600">{stat.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Entry Points Status */}
        <Card className="lg:col-span-1 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5 text-purple-600" />
              Entry Points
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {entryPoints.map((point) => {
              const Icon = point.icon;
              const isOpen = point.status === 'open';
              return (
                <motion.div
                  key={point.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    "p-4 rounded-lg border-2 transition-all",
                    isOpen 
                      ? "bg-emerald-50 border-emerald-300" 
                      : "bg-gray-50 border-gray-200"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        isOpen ? "bg-emerald-500" : "bg-gray-400"
                      )}>
                        {isOpen ? (
                          <DoorOpen className="w-5 h-5 text-white" />
                        ) : (
                          <DoorClosed className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{point.name}</div>
                        <div className="text-xs text-gray-500">
                          {isOpen ? 'Unlocked' : 'Locked'}
                        </div>
                      </div>
                    </div>
                    <Badge variant={isOpen ? "default" : "secondary"} className={isOpen ? "bg-emerald-500" : ""}>
                      {isOpen ? 'Open' : 'Closed'}
                    </Badge>
                  </div>
                </motion.div>
              );
            })}
          </CardContent>
        </Card>

        {/* Live Access Events */}
        <Card className="lg:col-span-2 bg-white shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Activity className="w-5 h-5 text-purple-600" />
                Live Access Events
              </CardTitle>
              <Badge className="bg-emerald-500 text-white text-[10px]">
                <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse" />
                LIVE
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              <AnimatePresence>
                {accessEvents.map((event) => {
                  const EventIcon = event.icon;
                  const isGranted = event.status === 'granted';
                  return (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className={cn(
                        "p-3 rounded-lg border-l-4 flex items-center gap-3",
                        isGranted 
                          ? "bg-emerald-50 border-emerald-500" 
                          : "bg-red-50 border-red-500"
                      )}
                    >
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center",
                        isGranted ? "bg-emerald-500" : "bg-red-500"
                      )}>
                        <EventIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-sm text-gray-900">{event.user}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-2">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-medium text-gray-900">{event.time}</div>
                            <Badge 
                              variant="outline" 
                              className={cn(
                                "text-[10px] mt-1",
                                isGranted 
                                  ? "border-emerald-300 text-emerald-700" 
                                  : "border-red-300 text-red-700"
                              )}
                            >
                              {event.method}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Access Statistics Chart */}
      <Card className="bg-white shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            Access Statistics (Last 24 Hours)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={accessByHour} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="hour" fontSize={10} stroke="#9ca3af" />
              <YAxis fontSize={10} stroke="#9ca3af" />
              <Tooltip contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
              <Bar dataKey="granted" fill="#10b981" radius={[3, 3, 0, 0]} name="Granted" />
              <Bar dataKey="denied" fill="#ef4444" radius={[3, 3, 0, 0]} name="Denied" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Unified Platform Integration */}
      <Card className="bg-gradient-to-br from-purple-900 to-purple-800 text-white shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Unified Platform Integration
          </CardTitle>
          <CardDescription className="text-purple-200">
            Access Control managed alongside Diesel Generator and other industrial assets
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <Router className="w-6 h-6" />
                <span className="font-semibold">Gateway Connected</span>
              </div>
              <p className="text-sm text-purple-200">ACC-CTL-0567 online via 4G</p>
            </div>
            <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <Key className="w-6 h-6" />
                <span className="font-semibold">Access Methods</span>
              </div>
              <p className="text-sm text-purple-200">Card, Biometric, Mobile App</p>
            </div>
            <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <Layers className="w-6 h-6" />
                <span className="font-semibold">One Platform</span>
              </div>
              <p className="text-sm text-purple-200">All assets managed together</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// SCADA Gateway Dashboard Component
function ScadaGatewayDashboard() {
  const [scadaData, setScadaData] = useState(generateScadaData());
  const [provOpen, setProvOpen] = useState(false);
  
  // Modbus RTU Devices
  const [modbusDevices, setModbusDevices] = useState([
    { id: 1, name: 'PLC-001 (Pump Station)', address: 1, status: 'online', protocol: 'Modbus RTU', baudRate: 9600, registers: 45, lastPoll: Date.now() },
    { id: 2, name: 'VFD-002 (Motor Drive)', address: 2, status: 'online', protocol: 'Modbus RTU', baudRate: 9600, registers: 32, lastPoll: Date.now() - 1200 },
    { id: 3, name: 'RTU-003 (Tank Level)', address: 3, status: 'online', protocol: 'Modbus RTU', baudRate: 19200, registers: 18, lastPoll: Date.now() - 500 },
    { id: 4, name: 'HMI-004 (Control Panel)', address: 4, status: 'warning', protocol: 'Modbus TCP', baudRate: null, registers: 64, lastPoll: Date.now() - 5000 },
  ]);

  // Analog Inputs (4-20mA)
  const [analogInputs, setAnalogInputs] = useState([
    { id: 'AI0', name: 'Pressure Transmitter', value: 4.5, unit: 'bar', min: 0, max: 10, status: 'normal' },
    { id: 'AI1', name: 'Flow Meter', value: 125.3, unit: 'L/min', min: 0, max: 200, status: 'normal' },
    { id: 'AI2', name: 'Tank Level', value: 67.2, unit: '%', min: 0, max: 100, status: 'normal' },
    { id: 'AI3', name: 'Temperature Sensor', value: 48.5, unit: '°C', min: 0, max: 100, status: 'warning' },
  ]);

  // Digital I/O
  const [digitalIO, setDigitalIO] = useState([
    { id: 'DI0', name: 'Emergency Stop', type: 'input', value: false, color: 'red' },
    { id: 'DI1', name: 'Pump Running', type: 'input', value: true, color: 'emerald' },
    { id: 'DI2', name: 'High Level Alarm', type: 'input', value: false, color: 'amber' },
    { id: 'DI3', name: 'Low Level Alarm', type: 'input', value: false, color: 'amber' },
    { id: 'DO0', name: 'Pump Start', type: 'output', value: true, color: 'blue' },
    { id: 'DO1', name: 'Valve Open', type: 'output', value: true, color: 'blue' },
    { id: 'DO2', name: 'Alarm Horn', type: 'output', value: false, color: 'red' },
    { id: 'DO3', name: 'Warning Light', type: 'output', value: false, color: 'amber' },
  ]);

  // Alarms
  const [alarms, setAlarms] = useState([
    { id: 1, time: '14:32:15', message: 'Temperature above setpoint (48.5°C > 45°C)', severity: 'warning', acknowledged: false },
    { id: 2, time: '14:28:00', message: 'HMI-004 communication delay detected', severity: 'warning', acknowledged: true },
    { id: 3, time: '13:45:22', message: 'System startup completed', severity: 'info', acknowledged: true },
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      // Update SCADA data
      setScadaData(prev => {
        const newPoint = {
          time: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
          pressure: Math.round((4.5 + Math.sin(Date.now() / 5000) * 1.5 + Math.random() * 0.3) * 100) / 100,
          flowRate: Math.round((120 + Math.cos(Date.now() / 7000) * 30 + Math.random() * 5) * 10) / 10,
          level: Math.round((65 + Math.sin(Date.now() / 8000) * 15 + Math.random() * 3) * 10) / 10,
          temperature: Math.round((45 + Math.sin(Date.now() / 5000) * 10 + Math.random() * 2) * 10) / 10,
          motorSpeed: Math.round(1450 + Math.sin(Date.now() / 6000) * 50 + Math.random() * 10),
          pumpStatus: 1,
          valvePosition: Math.round((50 + Math.cos(Date.now() / 9000) * 25 + Math.random() * 5) * 10) / 10,
        };
        return [...prev.slice(1), newPoint];
      });

      // Update analog inputs
      setAnalogInputs(prev => prev.map(input => ({
        ...input,
        value: input.id === 'AI0' ? Math.round((4.5 + Math.sin(Date.now() / 5000) * 1.5 + Math.random() * 0.3) * 100) / 100 :
               input.id === 'AI1' ? Math.round((125 + Math.cos(Date.now() / 7000) * 20 + Math.random() * 5) * 10) / 10 :
               input.id === 'AI2' ? Math.round((67 + Math.sin(Date.now() / 8000) * 8 + Math.random() * 2) * 10) / 10 :
               Math.round((48 + Math.sin(Date.now() / 5000) * 5 + Math.random() * 1) * 10) / 10,
        status: input.id === 'AI3' && (48 + Math.sin(Date.now() / 5000) * 5) > 47 ? 'warning' : 'normal'
      })));

      // Update Modbus device poll times
      setModbusDevices(prev => prev.map(device => ({
        ...device,
        lastPoll: device.status === 'online' ? Date.now() - Math.floor(Math.random() * 2000) : device.lastPoll,
        status: Math.random() > 0.02 ? device.status : (device.status === 'online' ? 'warning' : 'online')
      })));

    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const current = scadaData[scadaData.length - 1];

  const protocolStats = [
    { label: 'Modbus RTU', value: 3, icon: Radio, color: 'emerald', desc: 'RS-485 @ 9600/19200' },
    { label: 'Modbus TCP', value: 1, icon: Network, color: 'blue', desc: 'Ethernet 10.0.1.x' },
    { label: 'Analog Inputs', value: 4, icon: Waves, color: 'purple', desc: '4-20mA / 0-10V' },
    { label: 'Digital I/O', value: 8, icon: ToggleRight, color: 'amber', desc: '4 DI + 4 DO' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">SCADA IoT Gateway</h2>
          <p className="text-gray-500">General Purpose Gateway | IGW-SCADA-001 | Water Treatment Plant</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-cyan-500 text-white">
            <Cpu className="w-3 h-3 mr-1" />
            IGW-SCADA-001
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setProvOpen(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      <ProvisioningModal open={provOpen} onOpenChange={setProvOpen} assetLabel="SCADA Gateway" />

      {/* Protocol Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {protocolStats.map((stat) => {
          const Icon = stat.icon;
          const colorClasses = {
            emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600',
            blue: 'bg-blue-50 border-blue-200 text-blue-600',
            purple: 'bg-purple-50 border-purple-200 text-purple-600',
            amber: 'bg-amber-50 border-amber-200 text-amber-600',
          };
          return (
            <Card key={stat.label} className={cn("border-2", colorClasses[stat.color])}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-5 h-5" />
                  <span className="font-medium text-sm">{stat.label}</span>
                </div>
                <div className="text-2xl font-bold">{stat.value}</div>
                <div className="text-xs text-gray-500">{stat.desc}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Modbus Devices & Analog Inputs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Modbus RTU Devices */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Server className="w-5 h-5 text-cyan-600" />
              Connected Devices (Modbus)
            </CardTitle>
            <CardDescription>RS-485 & Ethernet field devices</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {modbusDevices.map((device) => (
              <motion.div
                key={device.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  "p-4 rounded-lg border-2 transition-all",
                  device.status === 'online' ? "bg-emerald-50 border-emerald-300" :
                  device.status === 'warning' ? "bg-amber-50 border-amber-300" :
                  "bg-red-50 border-red-300"
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      device.status === 'online' ? "bg-emerald-500" :
                      device.status === 'warning' ? "bg-amber-500" : "bg-red-500"
                    )}>
                      <HardDrive className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{device.name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-2">
                        <span>Addr: {device.address}</span>
                        <span>•</span>
                        <span>{device.protocol}</span>
                        {device.baudRate && <><span>•</span><span>{device.baudRate} baud</span></>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={device.status === 'online' ? "default" : "secondary"} className={device.status === 'online' ? "bg-emerald-500" : device.status === 'warning' ? "bg-amber-500" : ""}>
                      {device.status}
                    </Badge>
                    <div className="text-[10px] text-gray-500 mt-1">{device.registers} registers</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </CardContent>
        </Card>

        {/* Analog Inputs */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Waves className="w-5 h-5 text-purple-600" />
              Analog Inputs (4-20mA)
            </CardTitle>
            <CardDescription>Real-time sensor readings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {analogInputs.map((input) => {
              const percentage = ((input.value - input.min) / (input.max - input.min)) * 100;
              return (
                <div key={input.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{input.id}</Badge>
                      <span className="text-sm font-medium">{input.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "text-lg font-bold",
                        input.status === 'warning' ? "text-amber-600" : "text-gray-900"
                      )}>
                        {input.value} {input.unit}
                      </span>
                      {input.status === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                    </div>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      className={cn(
                        "h-full rounded-full",
                        input.status === 'warning' ? "bg-amber-500" : "bg-cyan-500"
                      )}
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-400">
                    <span>{input.min} {input.unit}</span>
                    <span>{input.max} {input.unit}</span>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Digital I/O Status */}
      <Card className="bg-white shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CircuitBoard className="w-5 h-5 text-amber-600" />
            Digital I/O Status
          </CardTitle>
          <CardDescription>Discrete inputs and outputs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {digitalIO.map((io) => {
              const colorMap = {
                red: io.value ? 'bg-red-500' : 'bg-red-200',
                emerald: io.value ? 'bg-emerald-500' : 'bg-emerald-200',
                amber: io.value ? 'bg-amber-500' : 'bg-amber-200',
                blue: io.value ? 'bg-blue-500' : 'bg-blue-200',
              };
              return (
                <div
                  key={io.id}
                  className={cn(
                    "p-4 rounded-lg border-2 transition-all",
                    io.value ? "border-gray-300 bg-gray-50" : "border-gray-200 bg-white"
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="text-[10px]">{io.id}</Badge>
                    <div className={cn("w-3 h-3 rounded-full", colorMap[io.color])} />
                  </div>
                  <div className="text-sm font-medium text-gray-900">{io.name}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-gray-500 uppercase">{io.type}</span>
                    {io.value ? (
                      <ToggleRight className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <ToggleLeft className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Process Variables */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-cyan-600" />
              Process Variables
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={scadaData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="pressureGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="flowGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" fontSize={10} stroke="#9ca3af" />
                <YAxis fontSize={10} stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                <Area type="monotone" dataKey="pressure" stroke="#06b6d4" strokeWidth={2} fill="url(#pressureGrad)" name="Pressure (bar)" />
                <Area type="monotone" dataKey="level" stroke="#8b5cf6" strokeWidth={2} fill="url(#flowGrad)" name="Tank Level (%)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Motor & Valve */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <GaugeCircle className="w-5 h-5 text-amber-600" />
              Motor Speed & Valve Position
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={scadaData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" fontSize={10} stroke="#9ca3af" />
                <YAxis yAxisId="left" fontSize={10} stroke="#9ca3af" />
                <YAxis yAxisId="right" orientation="right" fontSize={10} stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                <Line yAxisId="left" type="monotone" dataKey="motorSpeed" stroke="#f59e0b" strokeWidth={2} dot={false} name="Motor Speed (RPM)" />
                <Line yAxisId="right" type="monotone" dataKey="valvePosition" stroke="#10b981" strokeWidth={2} dot={false} name="Valve Position (%)" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Alarms & Gateway Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Alarms */}
        <Card className="lg:col-span-2 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="w-5 h-5 text-red-600" />
              Active Alarms & Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alarms.map((alarm) => (
                <div
                  key={alarm.id}
                  className={cn(
                    "p-3 rounded-lg border-l-4 flex items-center gap-3",
                    alarm.severity === 'warning' ? "bg-amber-50 border-amber-500" :
                    alarm.severity === 'error' ? "bg-red-50 border-red-500" :
                    "bg-blue-50 border-blue-500"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center",
                    alarm.severity === 'warning' ? "bg-amber-500" :
                    alarm.severity === 'error' ? "bg-red-500" : "bg-blue-500"
                  )}>
                    {alarm.severity === 'info' ? (
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{alarm.message}</div>
                    <div className="text-xs text-gray-500">{alarm.time}</div>
                  </div>
                  <Badge variant={alarm.acknowledged ? "secondary" : "outline"} className={!alarm.acknowledged ? "border-amber-400 text-amber-700" : ""}>
                    {alarm.acknowledged ? 'ACK' : 'New'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Gateway Status */}
        <Card className="bg-gradient-to-br from-cyan-600 to-cyan-700 text-white shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Cpu className="w-8 h-8" />
              <Badge className="bg-white/20 text-white">Online</Badge>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-sm opacity-80">Gateway ID</div>
                <div className="font-semibold">IGW-SCADA-001</div>
              </div>
              <div>
                <div className="text-sm opacity-80">Uptime</div>
                <div className="font-semibold">14d 6h 32m</div>
              </div>
              <div>
                <div className="text-sm opacity-80">Poll Cycle</div>
                <div className="font-semibold">500ms</div>
              </div>
              <div>
                <div className="text-sm opacity-80">Data Points</div>
                <div className="font-semibold">159 tags</div>
              </div>
              <div className="pt-2 border-t border-white/20">
                <div className="flex items-center gap-2">
                  <Signal className="w-4 h-4" />
                  <span className="text-sm">4G LTE: -58 dBm</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <RefreshCw className="w-4 h-4" />
                  <span className="text-sm">Last sync: {new Date().toLocaleTimeString()}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unified Platform */}
      <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            SCADA to Cloud Integration
          </CardTitle>
          <CardDescription className="text-slate-300">
            Bridging legacy SCADA systems with modern IoT cloud infrastructure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <Radio className="w-6 h-6" />
                <span className="font-semibold">Modbus RTU</span>
              </div>
              <p className="text-sm text-slate-300">RS-485 serial communication</p>
            </div>
            <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <Network className="w-6 h-6" />
                <span className="font-semibold">Modbus TCP</span>
              </div>
              <p className="text-sm text-slate-300">Ethernet connectivity</p>
            </div>
            <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <Waves className="w-6 h-6" />
                <span className="font-semibold">Analog I/O</span>
              </div>
              <p className="text-sm text-slate-300">4-20mA & 0-10V signals</p>
            </div>
            <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <Database className="w-6 h-6" />
                <span className="font-semibold">Cloud Historian</span>
              </div>
              <p className="text-sm text-slate-300">Time-series data storage</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Fleet Management Dashboard Component
function FleetManagementDashboard() {
  const [provOpen, setProvOpen] = useState(false);
  
  // Vehicle fleet data
  const [vehicles, setVehicles] = useState([
    { id: 'VH-001', name: 'Delivery Truck #1', type: 'truck', driver: 'Youcef Benali', status: 'moving', speed: 72, fuel: 68, lat: 36.7538, lng: 3.0588, location: 'N5, Algiers', lastUpdate: Date.now() },
    { id: 'VH-002', name: 'Service Van #1', type: 'van', driver: 'Karim Hadj', status: 'idle', speed: 0, fuel: 45, lat: 36.3650, lng: 6.6147, location: 'Constantine Center', lastUpdate: Date.now() - 120000 },
    { id: 'VH-003', name: 'Cargo Truck #2', type: 'truck', driver: 'Ahmed Saidi', status: 'moving', speed: 85, fuel: 82, lat: 35.6987, lng: -0.6349, location: 'A1 Highway, Oran', lastUpdate: Date.now() },
    { id: 'VH-004', name: 'Executive Car', type: 'car', driver: 'Nadia Mansouri', status: 'parked', speed: 0, fuel: 91, lat: 36.7650, lng: 5.0843, location: 'Béjaïa Port', lastUpdate: Date.now() - 3600000 },
    { id: 'VH-005', name: 'Delivery Truck #3', type: 'truck', driver: 'Mohamed Kaci', status: 'moving', speed: 65, fuel: 34, lat: 34.8503, lng: 5.7249, location: 'RN1, Ghardaia', lastUpdate: Date.now() },
  ]);

  // Trip history data
  const [tripHistory, setTripHistory] = useState(() => 
    Array.from({ length: 24 }, (_, i) => ({
      hour: `${String(i).padStart(2, '0')}:00`,
      distance: Math.floor(Math.random() * 150 + 50),
      trips: Math.floor(Math.random() * 8 + 2),
      fuelUsed: Math.round((Math.random() * 30 + 10) * 10) / 10,
    }))
  );

  // Alerts
  const [alerts, setAlerts] = useState([
    { id: 1, time: '14:45:22', vehicle: 'VH-003', type: 'speeding', message: 'Speed exceeded 80 km/h in urban zone', severity: 'warning' },
    { id: 2, time: '14:32:10', vehicle: 'VH-005', type: 'fuel', message: 'Fuel level below 35%', severity: 'warning' },
    { id: 3, time: '13:15:00', vehicle: 'VH-001', type: 'geofence', message: 'Entered Algiers Industrial Zone', severity: 'info' },
    { id: 4, time: '12:48:33', vehicle: 'VH-002', type: 'idle', message: 'Vehicle idle for more than 30 minutes', severity: 'warning' },
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setVehicles(prev => prev.map(vehicle => {
        if (vehicle.status === 'moving') {
          const speedChange = (Math.random() - 0.5) * 10;
          const newSpeed = Math.max(0, Math.min(120, vehicle.speed + speedChange));
          const fuelConsumption = newSpeed > 0 ? 0.02 : 0;
          return {
            ...vehicle,
            speed: Math.round(newSpeed),
            fuel: Math.max(0, Math.round((vehicle.fuel - fuelConsumption) * 10) / 10),
            lat: vehicle.lat + (Math.random() - 0.5) * 0.001,
            lng: vehicle.lng + (Math.random() - 0.5) * 0.001,
            lastUpdate: Date.now(),
          };
        }
        return vehicle;
      }));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const fleetStats = {
    total: vehicles.length,
    moving: vehicles.filter(v => v.status === 'moving').length,
    idle: vehicles.filter(v => v.status === 'idle').length,
    parked: vehicles.filter(v => v.status === 'parked').length,
  };

  const avgFuel = Math.round(vehicles.reduce((sum, v) => sum + v.fuel, 0) / vehicles.length);
  const totalDistance = 12847; // km today

  const statusColors = {
    moving: 'emerald',
    idle: 'amber',
    parked: 'gray',
  };

  const vehicleIcons = {
    truck: Truck,
    van: Car,
    car: Car,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Fleet Management</h2>
          <p className="text-gray-500">Real-time Vehicle Tracking | Algeria Operations</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-blue-500 text-white">
            <Truck className="w-3 h-3 mr-1" />
            {fleetStats.total} Vehicles
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setProvOpen(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      <ProvisioningModal open={provOpen} onOpenChange={setProvOpen} assetLabel="Fleet Tracker" />

      {/* Fleet Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="border-2 bg-blue-50 border-blue-200 text-blue-600">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Truck className="w-5 h-5" />
            </div>
            <div className="text-2xl font-bold">{fleetStats.total}</div>
            <div className="text-xs text-gray-600">Total Vehicles</div>
          </CardContent>
        </Card>
        <Card className="border-2 bg-emerald-50 border-emerald-200 text-emerald-600">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Play className="w-5 h-5" />
            </div>
            <div className="text-2xl font-bold">{fleetStats.moving}</div>
            <div className="text-xs text-gray-600">Moving</div>
          </CardContent>
        </Card>
        <Card className="border-2 bg-amber-50 border-amber-200 text-amber-600">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Pause className="w-5 h-5" />
            </div>
            <div className="text-2xl font-bold">{fleetStats.idle}</div>
            <div className="text-xs text-gray-600">Idle</div>
          </CardContent>
        </Card>
        <Card className="border-2 bg-gray-50 border-gray-200 text-gray-600">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Square className="w-5 h-5" />
            </div>
            <div className="text-2xl font-bold">{fleetStats.parked}</div>
            <div className="text-xs text-gray-600">Parked</div>
          </CardContent>
        </Card>
        <Card className="border-2 bg-purple-50 border-purple-200 text-purple-600">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Route className="w-5 h-5" />
            </div>
            <div className="text-2xl font-bold">{totalDistance.toLocaleString()}</div>
            <div className="text-xs text-gray-600">km Today</div>
          </CardContent>
        </Card>
        <Card className="border-2 bg-cyan-50 border-cyan-200 text-cyan-600">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Fuel className="w-5 h-5" />
            </div>
            <div className="text-2xl font-bold">{avgFuel}%</div>
            <div className="text-xs text-gray-600">Avg Fuel</div>
          </CardContent>
        </Card>
      </div>

      {/* Map & Vehicle List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map View */}
        <Card className="lg:col-span-2 bg-white shadow-lg overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Map className="w-5 h-5 text-blue-600" />
              Live Fleet Map
            </CardTitle>
            <CardDescription>Real-time vehicle positions across Algeria</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="h-[400px] relative">
              <iframe
                title="Fleet Map"
                width="100%"
                height="100%"
                frameBorder="0"
                scrolling="no"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-2.5%2C19%2C12%2C37.5&layer=mapnik&marker=36.75%2C3.05"
                style={{ border: 0 }}
              />
              <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-gray-700">Moving ({fleetStats.moving})</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-gray-700">Idle ({fleetStats.idle})</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-gray-400" />
                  <span className="text-gray-700">Parked ({fleetStats.parked})</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle List */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Truck className="w-5 h-5 text-blue-600" />
              Vehicle Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
            {vehicles.map((vehicle) => {
              const VehicleIcon = vehicleIcons[vehicle.type] || Car;
              const statusColor = statusColors[vehicle.status];
              return (
                <motion.div
                  key={vehicle.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    "p-4 rounded-lg border-2 transition-all",
                    statusColor === 'emerald' ? "bg-emerald-50 border-emerald-300" :
                    statusColor === 'amber' ? "bg-amber-50 border-amber-300" :
                    "bg-gray-50 border-gray-200"
                  )}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center",
                        statusColor === 'emerald' ? "bg-emerald-500" :
                        statusColor === 'amber' ? "bg-amber-500" : "bg-gray-400"
                      )}>
                        <VehicleIcon className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{vehicle.name}</div>
                        <div className="text-[10px] text-gray-500">{vehicle.id}</div>
                      </div>
                    </div>
                    <Badge 
                      variant="secondary" 
                      className={cn(
                        "text-[10px] capitalize",
                        statusColor === 'emerald' ? "bg-emerald-100 text-emerald-700" :
                        statusColor === 'amber' ? "bg-amber-100 text-amber-700" :
                        "bg-gray-100 text-gray-700"
                      )}
                    >
                      {vehicle.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="flex items-center gap-1">
                      <Gauge className="w-3 h-3 text-gray-400" />
                      <span className="font-medium">{vehicle.speed} km/h</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Fuel className="w-3 h-3 text-gray-400" />
                      <span className={cn("font-medium", vehicle.fuel < 35 && "text-amber-600")}>{vehicle.fuel}%</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3 text-gray-400" />
                      <span className="truncate">{vehicle.driver.split(' ')[0]}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-2 text-[10px] text-gray-500">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{vehicle.location}</span>
                  </div>
                </motion.div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Charts & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distance & Trips Chart */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Daily Activity (Last 24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={tripHistory.slice(-12)} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="hour" fontSize={10} stroke="#9ca3af" />
                <YAxis fontSize={10} stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                <Bar dataKey="distance" fill="#3b82f6" radius={[3, 3, 0, 0]} name="Distance (km)" />
                <Bar dataKey="fuelUsed" fill="#f59e0b" radius={[3, 3, 0, 0]} name="Fuel Used (L)" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Alerts Panel */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bell className="w-5 h-5 text-amber-600" />
                Fleet Alerts
              </CardTitle>
              <Badge className="bg-amber-500 text-white">{alerts.filter(a => a.severity === 'warning').length} Active</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[220px] overflow-y-auto">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={cn(
                    "p-3 rounded-lg border-l-4 flex items-center gap-3",
                    alert.severity === 'warning' ? "bg-amber-50 border-amber-500" : "bg-blue-50 border-blue-500"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center",
                    alert.severity === 'warning' ? "bg-amber-500" : "bg-blue-500"
                  )}>
                    {alert.type === 'speeding' ? <Gauge className="w-4 h-4 text-white" /> :
                     alert.type === 'fuel' ? <Fuel className="w-4 h-4 text-white" /> :
                     alert.type === 'geofence' ? <MapPinned className="w-4 h-4 text-white" /> :
                     <AlertCircle className="w-4 h-4 text-white" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-700">{alert.vehicle}</span>
                      <Badge variant="outline" className="text-[9px]">{alert.type}</Badge>
                    </div>
                    <div className="text-xs text-gray-600">{alert.message}</div>
                    <div className="text-[10px] text-gray-400 mt-1">{alert.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Driver Performance */}
      <Card className="bg-white shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-purple-600" />
            Driver Performance Today
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {vehicles.map((vehicle) => (
              <div key={vehicle.id} className="p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                    {vehicle.driver.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{vehicle.driver}</div>
                    <div className="text-[10px] text-gray-500">{vehicle.id}</div>
                  </div>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Distance</span>
                    <span className="font-medium">{Math.floor(Math.random() * 200 + 50)} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Trips</span>
                    <span className="font-medium">{Math.floor(Math.random() * 8 + 2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Score</span>
                    <Badge variant="outline" className={cn(
                      "text-[10px]",
                      Math.random() > 0.3 ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"
                    )}>
                      {Math.floor(Math.random() * 20 + 80)}/100
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Unified Platform */}
      <Card className="bg-gradient-to-br from-blue-900 to-blue-800 text-white shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Unified Fleet & Asset Management
          </CardTitle>
          <CardDescription className="text-blue-200">
            Complete visibility across vehicles, generators, and industrial assets
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <MapPinned className="w-6 h-6" />
                <span className="font-semibold">GPS Tracking</span>
              </div>
              <p className="text-sm text-blue-200">Real-time location updates</p>
            </div>
            <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <Route className="w-6 h-6" />
                <span className="font-semibold">Route History</span>
              </div>
              <p className="text-sm text-blue-200">Complete trip playback</p>
            </div>
            <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <Bell className="w-6 h-6" />
                <span className="font-semibold">Smart Alerts</span>
              </div>
              <p className="text-sm text-blue-200">Speeding, geofence, fuel</p>
            </div>
            <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <Layers className="w-6 h-6" />
                <span className="font-semibold">One Platform</span>
              </div>
              <p className="text-sm text-blue-200">Fleet + Industrial IoT</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Demo Dashboard Component
export default function DemoPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [activeAsset, setActiveAsset] = useState('diesel-generator');

  const currentAsset = ASSETS.find(a => a.id === activeAsset);

  return (
    <div className="min-h-screen bg-gray-50">
      <HomeHeader />
      
      <SidebarProvider>
        <div className="flex pt-20">
          {/* Sidebar */}
          <Sidebar 
            className="border-r border-gray-200 bg-white"
            collapsible="none"
          >
            <div className="h-[calc(100vh-5rem)] overflow-y-auto">
              <SidebarHeader className="p-4 border-b sticky top-0 bg-white z-10">
                <div className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-blue-600" />
                  <h2 className="font-bold text-lg text-gray-900">Demo Assets</h2>
                </div>
                <p className="text-xs text-gray-500 mt-1">Switch between assets</p>
              </SidebarHeader>
              <SidebarContent className="p-2">
                <SidebarGroup>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {ASSETS.map((asset) => {
                        const Icon = asset.icon;
                        const isActive = activeAsset === asset.id;
                        return (
                          <SidebarMenuItem key={asset.id}>
                            <SidebarMenuButton
                              onClick={() => setActiveAsset(asset.id)}
                              className={cn(
                                "w-full justify-start gap-3",
                                isActive 
                                  ? "bg-blue-50 text-blue-600 border-l-4 border-blue-600" 
                                  : "text-gray-600 hover:bg-gray-50"
                              )}
                            >
                              <Icon className="w-5 h-5" />
                              <span className="font-medium">{asset.name}</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              </SidebarContent>
            </div>
          </Sidebar>

          {/* Main Content */}
          <SidebarInset className="flex-1 overflow-auto min-h-[calc(100vh-5rem)]">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeAsset}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {activeAsset === 'diesel-generator' && <DieselGeneratorDashboard />}
                  {activeAsset === 'factory-access' && <FactoryAccessControlDashboard />}
                  {activeAsset === 'scada-gateway' && <ScadaGatewayDashboard />}
                  {activeAsset === 'fleet-management' && <FleetManagementDashboard />}
                </motion.div>
              </AnimatePresence>
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-16 mt-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Experience the full power of Al Saqr IoT Platform with your own industrial assets. 
            Contact us for a personalized demo or start your free trial today.
          </p>
          <div className="flex justify-center gap-4">
            <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600">
              Start Free Trial
            </Button>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
              Contact Sales
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
